import React, { useEffect, useState } from "react";
import { Card, CardContent, Grid } from "@mui/material";
import CounterCard from "../../components/CounterCard";
import {
  getCompletedInvoiceDetails,
  getInvoiceDetails,
} from "../../services/dataService";
import InvoicesTable from "../../components/table/InvoicesTable";
import { dateBeforeMonths, today } from "../../manager/dateManager";
import {
  INVOICE_COLUMN,
  GENERATED_INVOICE_COLUMN,
} from "../../columns/columns";
import { createInvoiceCards } from "../../manager/cardManager";
import {
  dispatcher,
  getGeneratedInvoicesFromStore,
  requestCompletedInvoice,
} from "../../store/helper";
import {
  GET_COMPLETED_INVOICES,
  GET_GENERATED_INVOICES,
} from "../../store/ActionType";
import store from "../../store/Store";
import { invoiceDataCreator } from "../../helpers/dateExtractor";
import { getUser } from "../../services/authService";


function Invoices() {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(dateBeforeMonths(4));
  const [toDate, setToDate] = useState(today());
  const [overviewItems, setOverviewItems] = useState(
    createInvoiceCards("loading")
  );
  const [activeCard, setActiveCard] = useState(
    localStorage.getItem("ACCEPTED PO") || "ACCEPTED PO"
  );
  const user = getUser();
  const getInvoicesDetails = async () => {
    try {
      setLoading(true);
      const response = await getInvoiceDetails({
        FromDate: fromDate,
        ToDate: toDate,
      });
      dispatcher(GET_GENERATED_INVOICES, response.data);
      setLoading(false);
      return response.data;
    } catch (error) {
      setLoading(false);
      setTableData([]);
      if (error.response && error.response.status === 404) {
        setOverviewItems(createInvoiceCards(404));
      } else {
        setOverviewItems(createInvoiceCards("error"));
      }
    }
  };

  const getCompletedInvoices = async () => {
    try {
      setLoading(true);
      const response = await getCompletedInvoiceDetails({
        FromDate: fromDate,
        ToDate: toDate,
        isInvoicingDone: true,
      });
      setLoading(false);
      return response.data;
    } catch (error) {
      setLoading(false);
      setTableData([]);
      if (error.response && error.response.status === 404) {
        setOverviewItems(createInvoiceCards(404));
      } else {
        setOverviewItems(createInvoiceCards("error"));
      }
      return [];
    }
  };

  const getPartialInvoices = async () => {
    try {
      setLoading(true);
      const response = await getCompletedInvoiceDetails({
        FromDate: fromDate,
        ToDate: toDate,
        isInvoicingDone: false,
      });
      setLoading(false);
      return response.data;
    } catch (error) {
      setLoading(false);
      setTableData([]);
      if (error.response && error.response.status === 404) {
        setOverviewItems(createInvoiceCards(404));
      } else {
        setOverviewItems(createInvoiceCards("error"));
      }
      return [];
    }
  };

  const sortingHandler = (status) => {
    localStorage.setItem("ACCEPTED PO", status);
    const invoicesData = requestCompletedInvoice();
    const generatedInvoiceData = getGeneratedInvoicesFromStore();
    setActiveCard(status);
    if (status === "ACCEPTED PO") {
      setTableData(invoicesData?.partial);
    } else if (status === "Uploaded Invoices") {
      setTableData(generatedInvoiceData);
    } else if (status === "Completed PO") {
      setTableData(invoicesData?.completed);
    }
    setOverviewItems(
      createInvoiceCards([
        invoicesData?.partial.length,
        generatedInvoiceData.length,
        invoicesData?.completed.length,
      ])
    );
  };

  const getInvoicesData = async () => {
    const ispartialInvoices = await getPartialInvoices();
    const isCompleted = await getCompletedInvoices();

    const generatedCount = await getInvoicesDetails();

    dispatcher(GET_COMPLETED_INVOICES, {
      partial: [...ispartialInvoices],
      completed: [...isCompleted],
    });

    setOverviewItems(
      createInvoiceCards([
        ispartialInvoices.length,
        generatedCount.length,
        isCompleted.length,
      ])
    );
  };

  useEffect(() => {
    const generatedInvoiceData = getGeneratedInvoicesFromStore();
    getInvoicesData();

    if (generatedInvoiceData.length !== 0) {
      sortingHandler(activeCard);
    } else {
      getInvoicesData();
    }

    const unsubscribe = store.subscribe(() => {
      sortingHandler(activeCard);
    });
    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getColumns = () => {
    if (activeCard === "ACCEPTED PO") {
      return [
        ...INVOICE_COLUMN,
        {
          Header: "UPLOAD INVOICE",
          accessor: "action",
          show: true,
          exportable: true,
        },
      ];
    } else if (activeCard === "Uploaded Invoices" && !user.isSystemAdmin) {
      return [
        ...GENERATED_INVOICE_COLUMN,
        {
          Header: "VIEW INVOICE",
          accessor: "action",
          show: true,
          exportable: true,
        },
      ];
    } else if (activeCard === "Uploaded Invoices" && user.isSystemAdmin) {
      return [
        ...GENERATED_INVOICE_COLUMN,
        {
          Header: "ACTIONS",
          accessor: "superaction",
          show: true,
          exportable: true,
        },
      ];
    } else if (activeCard === "Completed PO") {
      return [
        ...INVOICE_COLUMN,
        {
          Header: "VIEW INVOICE",
          accessor: "action",
          show: true,
          exportable: true,
        },
      ];
    }
  };

  return (
    <>
      <Grid container spacing={2} marginTop={0}>
        <Grid container spacing={2}>
          {overviewItems.map((item, index) => (
            <Grid key={index} item xs={6} md={3}>
              <CounterCard
                description={item.name}
                title={item.value}
                icon={item.icon}
                color={item.dark}
                bgColor={item.light}
                isActive={activeCard === item.name}
                onClickHandler={() => {
                  sortingHandler(item.name);
                }}
              />
            </Grid>
          ))}
          <Grid item xs={12} md={12}>
            <Card>
              <CardContent>
                <InvoicesTable
                  data={invoiceDataCreator(tableData)}
                  columns={getColumns()}
                  fromDate={fromDate}
                  toDate={toDate}
                  handleFromDate={(e) => setFromDate(e.target.value)}
                  handleToDate={(e) => setToDate(e.target.value)}
                  callAPI={getInvoicesData}
                  expandedTable={true}
                  loading={loading}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

export default Invoices;
